"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"
import { Textarea } from "../../components/ui/textarea"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select"
import { useToast } from "../../components/ui/use-toast"
import Sidebar from "./sidebar"
import { DEFAULT_IMAGE } from "../../data/TeachersList"
import { de, te } from "date-fns/locale"
import { Edit, Plus, Trash2, ArrowUp, ArrowDown, X } from "lucide-react"
import Pagination from "../../components/Pagination";

const DEPARTMENTS = ["hudebni", "tanecni", "vytvarny", "administrativa"] as const;
type Department = (typeof DEPARTMENTS)[number];

interface Employee {
  id: number;
  name: string;
  teaches: string;
  resume: string;
  department: Department;
  photo: string;
}

const ZamestnanciPage = () => {
  const [employees, setEmployees] = useState<Employee[]>([])

  const fetchZamestnanci = async () => {
      const response = await fetch("https://preview.zushm.cz/api/zamestanci/");
      if (!response.ok) throw new Error("Failed to fetch data");
      const rawData = await response.json();

      // Process the data to match the required format
      const processedEmployees: Employee[] = rawData.map((teacher: any) => ({
        id: Number(teacher.ID),
        name: teacher.Jmeno,
        teaches: teacher.Vyucuje,
        resume: teacher.Zivotopis,
        department: teacher.Obor as Department,
        photo: teacher.Image.split(",")[0] || DEFAULT_IMAGE,
      }));

      setEmployees(processedEmployees);
  };

  useEffect(() => {
    fetchZamestnanci();
  }, []);

  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedPhotos, setSelectedPhotos] = useState<File[]>([]);
  const [photoPreviewUrls, setPhotoPreviewUrls] = useState<string[]>([]);
  const [currentEmployee, setCurrentEmployee] = useState<Employee | null>(null)
  const [newEmployee, setNewEmployee] = useState<Partial<Employee>>({
    name: "",
    teaches: "",
    resume: "",
    department: "hudebni",
    photo: "",
  })
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const startIndex = (currentPage - 1) * rowsPerPage
  const endIndex = startIndex + rowsPerPage
  const visibleItems = employees.slice(startIndex, endIndex)

  const { toast } = useToast()

  useEffect(() => {
    const ROW_HEIGHT = 65
    let PADDING = 400

    const updateRows = () => {
      const availableHeight = window.innerHeight - PADDING

      const rows = Math.floor(availableHeight / 65) //Row height

      setRowsPerPage(Math.max(1, rows))
    }

    updateRows()
    window.addEventListener("resize", updateRows)
    return () => window.removeEventListener("resize", updateRows)
  }, [])

  const getDepartmentLabel = (department: Department) => {
    switch (department) {
      case "hudebni":
        return "Hudební"
      case "tanecni":
        return "Taneční"
      case "vytvarny":
        return "Výtvarný"
      default:
        return `${department}`
    }
  }

  const handleCreateEmployee = async () => {
    // Basic validation
    if (!newEmployee.name || !newEmployee.teaches || !newEmployee.resume || !newEmployee.department) {
      toast({
        title: "Chyba",
        description: "Prosím vyplňte všechna pole.",
        variant: "destructive",
      });
      return;
    }
  
    const token = document.cookie.split("; ").find(row => row.startsWith("token"));
    if (token) {
      const tokenValue = token.split("=")[1];
      const formData = new FormData();
      formData.append("jmeno", newEmployee.name);
      formData.append("vyucuje", newEmployee.teaches);
      formData.append("zivotopis", newEmployee.resume);
      formData.append("obor", newEmployee.department);
      selectedPhotos.forEach((photo, index) => {
        formData.append(`image${index}`, photo);
      });
  
      try {
        const response = await fetch("https://preview.zushm.cz/api/zamestanci/new", {
          method: "POST",
          headers: {
            "Authorization": tokenValue,
          },
          body: formData,
        });
  
        if (response.ok) {
          const data = await response.json();
          console.log("Employee created successfully", data);
        } else {
          console.error("Failed to create employee:", response.statusText);
        }
  
        // Close the dialog and reset the form
        setIsCreateDialogOpen(false);
        setNewEmployee({ name: "", teaches: "", resume: "", department: "hudebni", photo: "" });
        setSelectedPhotos([])
        setPhotoPreviewUrls([])
  
        // Fetch updated employee list after the creation is successful
        fetchZamestnanci();
  
      } catch (error) {
        console.error("Error creating employee:", error);
      }
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newPhotos = [...selectedPhotos];
    const newPreviews = [...photoPreviewUrls];
    
    const draggedItem = newPhotos[draggedIndex];
    newPhotos.splice(draggedIndex, 1);
    newPhotos.splice(index, 0, draggedItem);
      
    const draggedPreview = newPreviews[draggedIndex];
    newPreviews.splice(draggedIndex, 1);
    newPreviews.splice(index, 0, draggedPreview);
      
    setSelectedPhotos(newPhotos);
    setPhotoPreviewUrls(newPreviews);
    setDraggedIndex(index);
  };
  
  const handleDragEnd = () => {
    setDraggedIndex(null);
  };
  
  const openEditDialog = (employee: Employee) => {
    setCurrentEmployee(employee);
    setIsEditDialogOpen(true);
    // Resetovat vybrané fotky při otevření editace
    setSelectedPhotos([]);
    setPhotoPreviewUrls([]);
  }
  
  const openDeleteDialog = (employee: Employee) => {
    setCurrentEmployee(employee);
    setIsDeleteDialogOpen(true);
  }

  const handleEditEmployee = async () => {
    const token = document.cookie.split("; ").find(row => row.startsWith("token"));
  
    if (token) {
      const tokenValue = token.split("=")[1];
      try {
        const response = await fetch("https://preview.zushm.cz/api/zamestanci/update", {
          method: "POST",
          headers: {
            "Authorization": tokenValue,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id: currentEmployee?.id,
            jmeno: currentEmployee?.name,
            vyucuje: currentEmployee?.teaches,
            zivotopis: currentEmployee?.resume,
            obor: currentEmployee?.department,
          }),
        });
  
        if (response.ok) {
          const data = await response.json();
          console.log("Employee updated successfully", data);
        } else {
          console.error("Failed to update employee:", response.statusText);
        }
  
        // Close the edit dialog after the update
        setIsEditDialogOpen(false);
  
        // Fetch updated employee list after editing
        fetchZamestnanci();
  
      } catch (error) {
        console.error("Error updating employee:", error);
      }
    }
  };

  const handleDeleteEmployee = async () => {
    const token = document.cookie.split("; ").find(row => row.startsWith("token="));
  
    if (token) {
      const tokenValue = token.split("=")[1];
  
      try {
        const response = await fetch("https://preview.zushm.cz/api/zamestanci/delete", {
          method: "POST",
          headers: {
            "Authorization": tokenValue,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            jmeno: currentEmployee?.name
          }),
        });
  
        if (response.ok) {
          const data = await response.json();
          console.log("Employee deleted successfully", data);
        } else {
          console.error("Failed to delete employee:", response.statusText);
        }
  
        // Close the delete dialog after deletion
        setIsDeleteDialogOpen(false);
  
        // Fetch updated employee list after deletion
        fetchZamestnanci();
  
      } catch (error) {
        console.error("Error deleting employee:", error);
      }
    } else {
      toast({
        title: "Neúspěch",
        description: "Nejste přihlášeni.",
        variant: "destructive",
      });
    }
  };  

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []).slice(0, 3)

    setSelectedPhotos(files)

    const previews: string[] = new Array(files.length)

    files.forEach((file, index) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        previews[index] = reader.result as string

        if (previews.every(Boolean)) {
          setPhotoPreviewUrls(previews)
        }
      }
      reader.readAsDataURL(file)
    })
  }

  const removePhoto = (index: number) => {
    const newPhotos = [...selectedPhotos];
    newPhotos.splice(index, 1);
    setSelectedPhotos(newPhotos);

    const newPreviews = [...photoPreviewUrls];
    URL.revokeObjectURL(newPreviews[index]);
    newPreviews.splice(index, 1);
    setPhotoPreviewUrls(newPreviews);
  }

  const movePhoto = (index: number, direction: 'up' | 'down') => {
    const newPhotos = [...selectedPhotos];
    const newPreviews = [...photoPreviewUrls];
    
    if (direction === 'up' && index > 0) {
      [newPhotos[index], newPhotos[index - 1]] = [newPhotos[index - 1], newPhotos[index]];
      [newPreviews[index], newPreviews[index - 1]] = [newPreviews[index - 1], newPreviews[index]];
    } else if (direction === 'down' && index < newPhotos.length - 1) {
      [newPhotos[index], newPhotos[index + 1]] = [newPhotos[index + 1], newPhotos[index]];
      [newPreviews[index], newPreviews[index + 1]] = [newPreviews[index + 1], newPreviews[index]];
    }
    
    setSelectedPhotos(newPhotos);
    setPhotoPreviewUrls(newPreviews);
  }

  return (
    <div className="flex min-h-screen">
    <Sidebar></Sidebar>
    <div className="flex-1 pt-20 sm:p-6">
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-center sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold w-full text-center sm:text-left">Zaměstnanci</h1>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Přidat zaměstnance
        </Button>
      </div>

      {/* Employee list */}
      <Card>
        <CardHeader>
          <CardTitle>Seznam zaměstnanců</CardTitle>
          <CardDescription>Správa zaměstnanců a jejich informací</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[80px] hidden md:table-cell">Foto</TableHead>
                <TableHead>Jméno</TableHead>
                <TableHead className="hidden md:table-cell">Vyučuje</TableHead>
                <TableHead className="hidden md:table-cell">Obor</TableHead>
                <TableHead className="text-right">Akce</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {employees
                .slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
                .map((employee) => (
                  <TableRow key={employee.id}>
                    <TableCell className="hidden md:table-cell">
                      <div className="relative h-10 w-10 overflow-hidden rounded-full">
                        <img
                          src={employee.photo || "/placeholder.svg"}
                          alt={employee.name}
                          className="object-cover w-full h-full"
                        />
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{employee.name}</TableCell>
                    <TableCell className="hidden md:table-cell">{employee.teaches}</TableCell>
                    <TableCell className="hidden md:table-cell">{getDepartmentLabel(employee.department)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(employee)}
                        >
                          <Edit className="mr-2 h-4 w-4" />
                          Upravit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(employee)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>

          {/* Pagination */}
          <Pagination totalItems={employees.length} rowsPerPage={rowsPerPage} currentPage={currentPage} onPageChange={setCurrentPage} />
        </CardContent>
      </Card>


      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Přidat zaměstnance</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                Jméno
              </Label>
              <Input
                id="name"
                value={newEmployee.name || ""}
                onChange={(e) => setNewEmployee({ ...newEmployee, name: e.target.value })}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="teaches" className="text-right">
                Vyučuje
              </Label>
              <Input
                id="teaches"
                value={newEmployee.teaches || ""}
                onChange={(e) => setNewEmployee({ ...newEmployee, teaches: e.target.value })}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="department" className="text-right">
                Obor
              </Label>
              <Select onValueChange={(value) => setNewEmployee({ ...newEmployee, department: value as Department })}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Vyberte obor" defaultValue={newEmployee.department} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hudebni">Hudební</SelectItem>
                  <SelectItem value="tanecni">Taneční</SelectItem>
                  <SelectItem value="vytvarny">Výtvarný</SelectItem>
                  <SelectItem value="administrativa">Administrativa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="resume" className="text-right">
                Životopis
              </Label>
              <Textarea
                id="resume"
                value={newEmployee.resume || ""}
                onChange={(e) => setNewEmployee({ ...newEmployee, resume: e.target.value })}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="photo" className="text-right">
                Fotka
              </Label>
                <div className="col-span-3 space-y-4">
                  <div className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                    <label htmlFor="photo-upload" className="w-full h-full flex flex-col items-center justify-center cursor-pointer">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <Plus className="w-8 h-8 mb-3 text-gray-400" />
                        <p className="mb-2 text-sm text-gray-500">Přetáhněte fotky sem nebo klikněte pro výběr</p>
                        <p className="text-xs text-gray-500">
                          {selectedPhotos.length}/3 fotek
                        </p>
                      </div>
                      <input
                        id="photo-upload"
                        type="file"
                        className="hidden"
                        multiple
                        accept="image/*"
                        onChange={handlePhotoChange}
                        disabled={selectedPhotos.length >= 3}
                      />
                    </label>
                  </div>
                    
                  {photoPreviewUrls.length > 0 && (
                    <div className="mt-4">
                      <h3 className="text-sm font-medium mb-3">Náhled fotek (můžete změnit pořadí):</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {photoPreviewUrls.map((url, index) => (
                          <div 
                            key={index}
                            className="relative group border rounded-lg p-3 bg-gray-50"
                            draggable
                            onDragStart={() => handleDragStart(index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragEnd={handleDragEnd}
                          >
                          <div className="flex gap-2 mb-2">
                              <button
                                type="button"
                                onClick={() => movePhoto(index, 'up')}
                                disabled={index === 0}
                                className={`p-1 rounded ${index === 0 ? 'text-gray-300' : 'text-gray-700 hover:bg-gray-200'}`}
                              >
                                <ArrowUp className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => movePhoto(index, 'down')}
                                disabled={index === photoPreviewUrls.length - 1}
                                className={`p-1 rounded ${index === photoPreviewUrls.length - 1 ? 'text-gray-300' : 'text-gray-700 hover:bg-gray-200'}`}
                              >
                                <ArrowDown className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => removePhoto(index)}
                                className="p-1 rounded text-gray-700 hover:bg-gray-200 ml-auto"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                            <div className="aspect-square overflow-hidden rounded-lg bg-white">
                              <img
                                src={url}
                                alt={`Preview ${index + 1}`}
                                className="object-cover w-full h-full"
                              />
                            </div>
                          </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setIsCreateDialogOpen(false)}>
              Zrušit
            </Button>
            <Button type="submit" onClick={handleCreateEmployee}>
              Vytvořit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Upravit zaměstnance</DialogTitle>
            <DialogDescription>Upravte informace o vybraném zaměstnanci.</DialogDescription>
          </DialogHeader>
          {currentEmployee && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="name" className="text-right">
                  Jméno
                </Label>
                <Input
                  id="name"
                  value={currentEmployee.name || ""}
                  onChange={(e) => setCurrentEmployee({ ...currentEmployee, name: e.target.value })}
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="teaches" className="text-right">
                  Vyučuje
                </Label>
                <Input
                  id="teaches"
                  value={currentEmployee.teaches || ""}
                  onChange={(e) => setCurrentEmployee({ ...currentEmployee, teaches: e.target.value })}
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="department" className="text-right">
                  Obor
                </Label>
                <Select
                  onValueChange={(value) => setCurrentEmployee({ ...currentEmployee, department: value as Department })}
                >
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Vyberte obor" defaultValue={currentEmployee.department} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hudebni">Hudební</SelectItem>
                    <SelectItem value="tanecni">Taneční</SelectItem>
                    <SelectItem value="vytvarny">Výtvarný</SelectItem>
                    <SelectItem value="administrativa">Administrativa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="resume" className="text-right">
                  Životopis
                </Label>
                <Textarea
                  id="resume"
                  value={currentEmployee.resume || ""}
                  onChange={(e) => setCurrentEmployee({ ...currentEmployee, resume: e.target.value })}
                  className="col-span-3"
                />

              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setIsEditDialogOpen(false)}>
              Zrušit
            </Button>
            <Button type="submit" onClick={handleEditEmployee}>
              Uložit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Smazat zaměstnance</DialogTitle>
            <DialogDescription>Opravdu chcete smazat tohoto zaměstnance? Tato akce je nevratná.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setIsDeleteDialogOpen(false)}>
              Zrušit
            </Button>
            <Button type="submit" variant="destructive" onClick={handleDeleteEmployee}>
              Smazat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </div>
    </div>
  )
}

export default ZamestnanciPage